/**
 * Snippets Store Types
 *
 * Defines the shape of the centralized snippets state. The state is divided into:
 * 1. Data state: snippets array, isLoading, fetchError, createError, isLoaded
 * 2. Filter state: selectedCategoryId, searchQuery
 *
 * The `isLoaded` flag is crucial for preventing duplicate fetch requests -
 * once snippets are loaded, subsequent calls to fetchSnippets() will return
 * the cached data immediately.
 *
 * Error states are separated by operation type:
 * - `fetchError`: Set when fetching snippets fails
 * - `createError`: Set when creating a snippet fails
 */
import type { Snippet } from '../snippetTypes.ts';
import { DEFAULT_CATEGORY_ID } from '../constants/snippetCategories.ts';

export type SnippetsState = {
	/** Array of all snippets loaded from the server */
	snippets: Snippet[];
	/** True while a fetch or create operation is in progress */
	isLoading: boolean;
	/** Stores the most recent fetch error, null if no error */
	fetchError: Error | null;
	/** Stores the most recent create error, null if no error */
	createError: Error | null;
	/** True once snippets have been successfully loaded; prevents redundant fetches */
	isLoaded: boolean;
	// Filter state - used by useFilteredSnippets hook to compute filtered results
	/** Currently selected category filter ID */
	selectedCategoryId: string;
	/** Current search query text */
	searchQuery: string;
};

export const getInitialState = (): SnippetsState => ({
	snippets: [],
	isLoading: false,
	fetchError: null,
	createError: null,
	isLoaded: false,
	// Filter state defaults
	selectedCategoryId: DEFAULT_CATEGORY_ID,
	searchQuery: '',
});
